import { useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useParams, useNavigate } from 'react-router-dom';
import { getMediaList, getProblems, postProblems } from '../utils/api';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Save from '@mui/icons-material/Save';
import Cancel from '@mui/icons-material/Cancel';

import Loading from './loading';
import Image from './image';
import ShapeModal from './shape-modal';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  },
};

const ProblemEdit = () => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  let { id } = useParams();
  let navigate = useNavigate();
  const [p, setP] = useState(null);
  const [rev, setRev] = useState(0); // Revision counter to force updates/enable save
  const [visibleMedia, setVisibleMedia] = useState([]);
  const [showShapeModal, setShowShapeModal] = useState(false);
  const [showShapeModalIx, setShowShapeModalIx] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => setAccessToken(token));
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!isLoading) {
      if (id && parseInt(id) > 0) {
        getProblems(accessToken, parseInt(id)).then((data) => {
          setRev(0);
          setP(data[0]);
        });
      } else {
        setP({id: -1, type: 'Lett blanding', creator: '', shapes: [], grade: '6A', ticks: []});
      }
  
      getMediaList(accessToken).then((data) => {
        const visibleMedia = data.filter(m => !m.hidden && !m.carouselMedia);
        if (visibleMedia && visibleMedia.length > 0) {
          setVisibleMedia(visibleMedia);
          setShowShapeModal(false);
        } else {
          navigate('/media');
        }
      })
      .catch((error) => {
        console.warn(error);
        alert(error.toString());
      });
    }
  }, [id, isLoading, accessToken, navigate]);

  if (isLoading || !p || !visibleMedia || visibleMedia.length === 0) {
    return <Loading/>;
  }

  // Use a helper to update P cleanly
  const updateP = (field, value) => {
      const newP = { ...p };
      newP[field] = value;
      setP(newP);
      setRev(prev => prev + 1);
  }

  function onClick(x, y) {
    const newP = { ...p };
    newP.shapes.push({x: x, y: y, r: 20});
    setP(newP);
    setRev(rev + 1);
  }

  function removeShape(i) {
    const newP = { ...p };
    newP.shapes.splice(i, 1);
    setP(newP);
    setRev(rev + 1);
  }

  function save(del) {
    if (!del || confirm('Are you sure you want to delete problem?')) {
      const mediaId = p.mediaId ? p.mediaId : visibleMedia[0].id;
      postProblems(accessToken, p.id, mediaId, p.created, p.grade, p.type, p.creator, p.shapes, del)
      .then(() => {
        navigate("/");
      })
      .catch((error) => {
        console.warn(error);
        alert(error.toString());
      });
    }
  }

  function cancel() {
    if (rev > 0) {
      if(!confirm('Are you sure you want to leave page without saving?')) return;
    }
    navigate("/");
  }

  function openShapeModal(ix) {
    setShowShapeModal(true);
    setShowShapeModalIx(ix);
  }

  function closeShapeModal() {
    setShowShapeModal(false);
    setShowShapeModalIx(null);
  }

  function updateShape(text, r) {
    const newP = { ...p };
    newP.shapes[showShapeModalIx].text = text;
    newP.shapes[showShapeModalIx].r = r;
    
    setShowShapeModal(false);
    setShowShapeModalIx(null);
    setP(newP);
    setRev(rev + 1);
  }

  const currMedia = p.mediaId ? visibleMedia.filter(x => x.id === p.mediaId)[0] : visibleMedia[0];
  
  // Safe-guard if media not found
  if (!currMedia) return <Loading />;

  return (
    <div>
      <ShapeModal
        open={showShapeModal}
        text={showShapeModal && p.shapes[showShapeModalIx] ? (p.shapes[showShapeModalIx].text || "") : ""}
        r={showShapeModal && p.shapes[showShapeModalIx] ? p.shapes[showShapeModalIx].r : 20}
        onClose={closeShapeModal}
        onUpdate={updateShape}/>
      <form style={{display: 'flex', flexWrap: 'wrap'}} autoComplete="off">
        <FormControl style={{margin: 24, minWidth: 120}}>
          <InputLabel>Creator</InputLabel>
          <Input value={p.creator} onChange={(e) => updateP('creator', e.target.value)} />
        </FormControl>
        <FormControl style={{margin: 24, minWidth: 120}}>
          <InputLabel>Wall</InputLabel>
          <Select MenuProps={MenuProps} value={currMedia.id} onChange={(e) => updateP('mediaId', e.target.value)} input={<Input/>}>
            {visibleMedia.map((m, key) => <MenuItem key={key} value={m.id}>{m.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl style={{margin: 24, minWidth: 120}}>
          <InputLabel>Type</InputLabel>
          <Select MenuProps={MenuProps} value={p.type} onChange={(e) => updateP('type', e.target.value)} input={<Input/>}>
            <MenuItem value="Armgang">Armgang</MenuItem>
            <MenuItem value="Catch">Catch</MenuItem>
            <MenuItem value="Fingerjugs">Fingerjugs</MenuItem>
            <MenuItem value="Gode grep">Gode grep</MenuItem>
            <MenuItem value="Klemming">Klemming</MenuItem>
            <MenuItem value="Krimp">Krimp</MenuItem>
            <MenuItem value="Lett blanding">Lett blanding</MenuItem>
            <MenuItem value="Pinsj">Pinsj</MenuItem>
            <MenuItem value="Skulderflytt">Skulderflytt</MenuItem>
            <MenuItem value="Sloper">Sloper</MenuItem>
            <MenuItem value="Store flytt">Store flytt</MenuItem>
          </Select>
        </FormControl>
        <FormControl style={{margin: 24, minWidth: 120}}>
          <InputLabel>Grade</InputLabel>
          <Select MenuProps={MenuProps} value={p.grade} onChange={(e) => updateP('grade', e.target.value)} input={<Input/>}>
            <MenuItem value="6A">6A</MenuItem>
            <MenuItem value="6A+">6A+</MenuItem>
            <MenuItem value="6B">6B</MenuItem>
            <MenuItem value="6B+">6B+</MenuItem>
            <MenuItem value="6C">6C</MenuItem>
            <MenuItem value="6C+">6C+</MenuItem>
            <MenuItem value="7A">7A</MenuItem>
            <MenuItem value="7A+">7A+</MenuItem>
            <MenuItem value="7B">7B</MenuItem>
            <MenuItem value="7B+">7B+</MenuItem>
            <MenuItem value="7C">7C</MenuItem>
            <MenuItem value="7C+">7C+</MenuItem>
            <MenuItem value="8A">8A</MenuItem>
            <MenuItem value="8A+">8A+</MenuItem>
            <MenuItem value="8B">8B</MenuItem>
          </Select>
        </FormControl>
        <Button variant="text" color="primary" aria-label="save" disabled={rev === 0} onClick={() => save(false)}><Save /></Button>
        <Button variant="text" color="secondary" aria-label="cancel" onClick={cancel}><Cancel /></Button>
        <Button variant="text" color="secondary" aria-label="delete" disabled={p.id <= 0} onClick={() => save(true)}><Delete /></Button>
      </form>
      {p.shapes.map((s, i) => <Chip key={i} label={s.text ? s.r + "*" : s.r} avatar={<Avatar><Edit /></Avatar>} onDelete={() => removeShape(i)} onClick={() => openShapeModal(i)} />)}<br/>
      <Image mediaId={currMedia.id} shapesIn={p.shapes} width={currMedia.width} height={currMedia.height} maxHeight="100vh" onClick={onClick} alignLeft={true}/>
    </div>
  );
}

export default ProblemEdit;